import { API_BASE_URL } from '../config/apiConfig';
import { SessionManager } from '../redux/sessionManager';
import { ProgressEvent } from '../redux/api/documentsApi';

/**
 * Create a progress stream URL for SSE connection
 */
export function createProgressStreamUrl(tenantId: string, documentId: string): string {
  return `${API_BASE_URL}/api/v1/artefacts/${documentId}/progress/stream?tenant_id=${tenantId}`;
}

/**
 * Stream progress events from the server via Server-Sent Events (SSE)
 */
export async function* streamProgress(
  tenantId: string,
  documentId: string,
  signal?: AbortSignal
): AsyncGenerator<ProgressEvent, void, unknown> {
  const url = createProgressStreamUrl(tenantId, documentId);
  const authHeaders = SessionManager.getAuthHeaders();

  const headers: Record<string, string> = {
    'Accept': 'text/event-stream',
  };

  if (authHeaders.Authorization) {
    headers['Authorization'] = authHeaders.Authorization;
  }

  const response = await fetch(url, {
    headers,
    signal,
  });

  if (!response.ok) {
    throw new Error(`SSE connection failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data:')) {
          const data = line.slice(5).trim();
          if (data) {
            try {
              const event = JSON.parse(data) as ProgressEvent;
              yield event;

              // Stop streaming if completed or failed
              if (event.status === 'completed' || event.status === 'failed') {
                return;
              }
            } catch {
              // Ignore parse errors for non-JSON data
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

